import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import './dotglobal-change-badge.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

/**
 * AI activity digest card (violet "agent output" styling): a prose summary, stat chips,
 * and changelog highlights derived from the loaded tagged activity. Shared by the Explore
 * overview and the Org overview. Mirrors the Claude Design digest. Emits `dotglobal-toast`
 * (the regenerate/refresh affordance).
 */
@customElement('dotglobal-activity-digest')
export class DotglobalActivityDigest extends DeesElement {
  @property({ type: Array })
  public accessor activity: interfaces.IPublicActivityEntry[] = [];

  @property({ type: String })
  public accessor heading = "What's been shipped";

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .digest {
        position: relative;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        overflow: hidden;
      }
      .digest::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: linear-gradient(180deg, #8b5cf6, var(--accent));
      }
      .digest-h { display: flex; align-items: flex-start; gap: 10px; padding: 14px 16px 12px 17px; }
      .digest-title { font-family: var(--font-display); font-weight: 700; font-size: 15px; letter-spacing: -0.01em; }
      .digest-body { padding: 0 16px 14px; }
      .digest-prose { margin: 0; color: var(--text-primary); font-size: 13.5px; line-height: 1.65; }
      .digest-prose code {
        font-family: var(--font-mono);
        font-size: 12px;
        background: var(--code-bg);
        padding: 0.12em 0.36em;
        border-radius: 4px;
      }
      .digest-stats { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
      .digest-stat {
        display: flex;
        align-items: baseline;
        gap: 6px;
        padding: 6px 11px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-tertiary);
        font-size: 12px;
      }
      .digest-stat .n { color: var(--text-primary); font-family: var(--font-mono); font-size: 14px; font-weight: 700; }
      .digest-highlights {
        display: flex;
        flex-direction: column;
        margin-top: 18px;
        overflow: hidden;
        border: 1px solid var(--border-default);
        border-radius: 8px;
      }
      .digest-hl {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 16px;
        border-bottom: 1px solid var(--border-subtle);
        font-size: 13px;
      }
      .digest-hl:last-child { border-bottom: 0; }
      .hl-repo { flex-shrink: 0; color: var(--accent); font-family: var(--font-mono); font-size: 12px; }
      .hl-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        color: var(--text-secondary);
        font-size: 13.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .hl-age { flex-shrink: 0; color: var(--text-muted); font-size: 12.5px; }
      .digest-foot {
        padding: 9px 16px;
        border-top: 1px solid var(--border-subtle);
        background: var(--bg-secondary);
        font-size: 11.5px;
      }
      .iconbtn {
        width: 34px;
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        color: var(--text-secondary);
        cursor: pointer;
      }
      .iconbtn:hover { background: var(--hover); color: var(--text-primary); }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }
    `,
  ];

  public render(): TemplateResult {
    const activity = this.getUniqueSortedActivity(this.activity);
    const latest = activity[0];
    const repos = new Set(activity.map((entry) => entry.repoSlug));
    const versions = new Set(activity.map((entry) => `${entry.repoSlug}@${entry.tagName || entry.version}`));
    return html`
      <div class="digest">
        <div class="digest-h">
          <div class="agent-glyph">${this.icon('Tag')}</div>
          <div class="grow" style="min-width: 0">
            <div class="row">
              <span class="digest-title">${this.heading}</span>
              <span class="badge sq" style="color: #8b5cf6; background: color-mix(in srgb, #8b5cf6 16%, transparent)">${this.icon('Tag')} Changelog</span>
            </div>
            <div class="muted" style="font-size: 11.5px; margin-top: 3px">
              Tagged changelog entries · ${latest ? this.timeAgo(latest.createdAt) : 'no timestamp'}
            </div>
          </div>
          <button class="iconbtn" title="Refresh activity" @click=${() => this.toast('Activity reflects loaded tagged changelog entries')}>${this.icon('History')}</button>
        </div>
        <div class="digest-body">
          ${activity.length
            ? html`
                <p class="digest-prose">
                  Most recent tagged changelog activity spans <strong>${repos.size}</strong> repositories and <strong>${versions.size}</strong> versions.
                  The latest entry is <strong>${latest!.title}</strong> in <code>${latest!.repoSlug}</code>.
                </p>
                <div class="digest-stats">
                  <div class="digest-stat"><span class="n">${activity.length}</span><span class="muted">entries</span></div>
                  <div class="digest-stat"><span class="n">${repos.size}</span><span class="muted">repos</span></div>
                  <div class="digest-stat"><span class="n">${versions.size}</span><span class="muted">versions</span></div>
                </div>
                <div class="digest-highlights">
                  ${activity.slice(0, 4).map((entry) => this.renderHighlight(entry))}
                </div>
              `
            : html`<div class="empty">No tagged changelog activity available.</div>`}
        </div>
        <div class="digest-foot">
          <span class="muted">${this.icon('Tag')} Built from repository tags and matching changelog sections.</span>
        </div>
      </div>
    `;
  }

  private renderHighlight(entryArg: interfaces.IPublicActivityEntry): TemplateResult {
    return html`
      <div class="digest-hl">
        <dotglobal-change-badge .kind=${entryArg.kind}></dotglobal-change-badge>
        <span class="hl-repo">${entryArg.repoSlug.split('/').pop() || entryArg.repoSlug}</span>
        <span class="hl-text">${entryArg.title}</span>
        <span class="hl-age" title=${entryArg.tagName}>${entryArg.version || entryArg.tagName}</span>
      </div>
    `;
  }

  private getUniqueSortedActivity(activityArg: interfaces.IPublicActivityEntry[]): interfaces.IPublicActivityEntry[] {
    const seen = new Set<string>();
    return [...activityArg]
      .sort((entryA, entryB) => new Date(entryB.createdAt).getTime() - new Date(entryA.createdAt).getTime())
      .filter((entry) => {
        const key = entry.id || `${entry.repoSlug}|${entry.tagName}|${entry.title}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  private toast(messageArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-toast', { detail: { message: messageArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-activity-digest': DotglobalActivityDigest;
  }
}
